import { useState } from "react";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import {
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  Input,
  toast,
} from "@erp/ui";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useFormSubmit,
  useZodForm,
} from "@/components/form";
import { ApiError } from "@/lib/api/errors";
import { AUTH_CODES, completePasswordReset } from "@/lib/api/session";

import { AuthLayout } from "./auth-layout";

const MIN_PASSWORD_LENGTH = 12;

// Messages come from the forms kit's localized zod error map; the custom
// rule carries its i18n key via params per the kit convention.
const resetPasswordSchema = z
  .object({
    password: z.string().min(MIN_PASSWORD_LENGTH),
    confirm: z.string().min(1),
  })
  .refine((values) => values.password === values.confirm, {
    path: ["confirm"],
    params: { i18nKey: "validation.passwordMismatch" },
  });

export type ResetPasswordValues = z.infer<typeof resetPasswordSchema>;

type ResetError = "invalidToken" | "generic" | null;

export function ResetPasswordPage() {
  const { t } = useTranslation();
  const search = useSearch({ from: "/reset-password" });
  const navigate = useNavigate();
  const [resetError, setResetError] = useState<ResetError>(null);

  const form = useZodForm(resetPasswordSchema, {
    defaultValues: { password: "", confirm: "" },
  });

  const { onSubmit, isPending } = useFormSubmit(
    form,
    (values: ResetPasswordValues) => completePasswordReset(search.token ?? "", values.password),
    {
      onSuccess: async () => {
        toast({
          title: t(
            "auth.resetSuccess",
            "Your password has been reset. Sign in with your new password.",
          ),
          variant: "success",
        });
        await navigate({ to: "/login" });
      },
      // Field-level problems (e.g. auth.weak_password on "password") are
      // applied onto the form by the kit before this runs; only global
      // outcomes need rendering here.
      onError: (error) => {
        if (error instanceof ApiError && error.code === AUTH_CODES.invalidToken) {
          setResetError("invalidToken");
          return;
        }
        if (error instanceof ApiError && (error.problem?.errors?.length ?? 0) > 0) {
          setResetError(null);
          return;
        }
        setResetError("generic");
      },
    },
  );

  return (
    <AuthLayout>
      <Card className="w-full">
        <CardHeader>
          <CardTitle>
            <h1>{t("auth.resetPassword")}</h1>
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          {resetError === "invalidToken" && (
            <div
              role="alert"
              className="flex flex-col gap-2 rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm"
            >
              <span className="text-destructive">
                {t("auth.invalidToken", "This reset link is invalid or has expired.")}
              </span>
              <Link
                to="/forgot-password"
                className="text-primary underline-offset-4 hover:underline"
              >
                {t("auth.requestNewLink", "Request a new reset link")}
              </Link>
            </div>
          )}
          {resetError === "generic" && (
            <p
              role="alert"
              className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive"
            >
              {t("errors.generic")}
            </p>
          )}
          <Form form={form} onSubmit={onSubmit} className="flex flex-col gap-4">
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel required>{t("auth.newPassword")}</FormLabel>
                  <FormControl>
                    <Input type="password" autoComplete="new-password" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="confirm"
              render={({ field }) => (
                <FormItem>
                  <FormLabel required>{t("auth.confirmPassword")}</FormLabel>
                  <FormControl>
                    <Input type="password" autoComplete="new-password" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button type="submit" loading={isPending} className="w-full">
              {t("auth.resetPassword")}
            </Button>
          </Form>
        </CardContent>
        <CardFooter>
          <Link to="/login" className="text-sm text-primary underline-offset-4 hover:underline">
            {t("auth.backToLogin", "Back to sign in")}
          </Link>
        </CardFooter>
      </Card>
    </AuthLayout>
  );
}
